import { useCallback, useEffect, useRef, useState } from "react";

// How a Soru | Cevap pane copes with the fact that either side can be the taller
// one. Two outcomes, decided from measured heights:
//
//   "pin"  — this pane's inner block fits the scrollport and the pane itself is
//            stretched taller than it (because the SIBLING is the tall one). Pin
//            it so the learner keeps it in view while the other side scrolls.
//            Self-selecting: only the shorter side ever has slack, so only it
//            pins — the two hook instances need no coordination.
//
//   "none" — comparable panes, a single pane, stacked panes, or a block too tall
//            to pin safely.
//
// WHERE THE SCROLLPORT ACTUALLY IS
// Not `.aas-layout`. Its base rule looks like a scroller (height:100% +
// overflow-y:auto), but EVERY learner solve surface deliberately overrides that to
// `height:auto; overflow:visible` — their own comments give the reason: a popover
// (dropdown / matching) must not be clipped by an inner card scrollbar, so the
// card flows at content height and an OUTER pane scrolls as one.
//
// Seven of those outer panes really scroll, and are what a pin resolves against:
//   .rbd-activity-pane  corporate assignment
//   .rbl-activity-pane  personalized assignment
//   .rbc-solve-slot     contest
//   .rbq-scroll         corporate standard exam
//   .rbp-activity-pane  practise corporate assignment
//   .dl-panel           duel
//   .sax-activity-pane  support assignment (set solver)
// The practise REVIEW screen is the exception: `.rbo-question` sets no overflow at
// all (the `overflow-y:auto` nearby belongs to `.rbo-chat-body`, a SIBLING column,
// not an ancestor), so the walk finds no scroller there and that surface never
// pins — which is the intended outcome, since it already sticks its own chat panel.
//
// So the hook resolves the nearest ancestor that REALLY scrolls and pins against
// that. Do not "simplify" this to `.aas-layout` — it is never the scroller on a
// learner surface, and binding to it would silently disable pinning everywhere.
//
// DELIBERATELY NO "cap" MODE (admin-web's copy has one)
// admin-web also caps BOTH panes to the scrollport when both overflow it, because
// there the scroll ancestor is an auto-height modal body: uncapped, one pane runs
// off the screen and the other is lost. Here the outer pane is a real bounded
// scroller, so "both overflow" degrades on its own into the scrolls-as-one
// behaviour those screens explicitly ask for; capping would nest a second
// scrollbar inside it and re-introduce the popover clipping they removed. The
// `fits` guard below is what keeps that case safe: a block taller than the
// scrollport simply never pins.
//
// WHY offsetHeight IS SAFE HERE (admin-web cannot use it)
// admin-web has to reconstruct each card's NATURAL height (header + body
// scrollHeight) because its "cap" changes the rendered height of the very thing
// it measures — measuring the capped height would uncap on the next pass and
// oscillate forever. Pin-only has no such feedback loop: `position: sticky` keeps
// the element in normal flow and only offsets where it PAINTS, so offsetHeight is
// identical whether or not the pin is applied. That lets this hook read
// offsetHeight directly instead of depending on inner header/body class names,
// which is also why it needs no `.aas-pane-inner` internals knowledge.
export type PaneFitMode = "pin" | "none";

// A block must clear the scrollport by this much to count as "fits" — pinning
// something that only just fits gains nothing and flickers at the boundary.
const FIT_MARGIN = 16;
// Below this much slack the pin is imperceptible, so don't bother.
const MIN_SLACK = 24;

export interface PaneFitInput {
  /** Content height of THIS pane's inner block (the pane itself is stretched). */
  innerHeight: number;
  /** Height of the nearest scrolling ancestor (or the viewport). */
  portHeight: number;
  /** Rendered height of this pane — stretch-sized to the taller sibling. */
  paneHeight: number;
}

// The whole decision, as a pure function so it can be reasoned about and tested
// without a DOM. The hook below only measures and feeds it.
//
// Note there is no sibling height here, unlike admin-web: the sibling only ever
// mattered for the "cap" branch, and this pane's own numbers already encode the
// comparison — the pane is stretch-sized to the taller side, so `slack` IS the
// height difference between the two panes.
export const decidePaneFitMode = ({
  innerHeight,
  portHeight,
  paneHeight,
}: PaneFitInput): PaneFitMode => {
  // Pin only if the block FITS the scrollport — a sticky element taller than its
  // scrollport freezes with its lower half permanently unreachable, which for a
  // learner means an answer they cannot get to.
  const fits = innerHeight + FIT_MARGIN <= portHeight;
  const slack = paneHeight - innerHeight;
  return fits && slack >= MIN_SLACK ? "pin" : "none";
};

/**
 * Decides whether a Soru / Cevap pane should pin its content while the taller
 * sibling scrolls. Returns a ref to put on the PANE (whose first element child
 * must be the inner block) and whether it is currently pinned.
 *
 * `enabled` is opt-in per template — see ActivitySolveLayout's `paneFit` prop.
 */
export const usePaneFitMode = (enabled: boolean) => {
  const paneRef = useRef<HTMLDivElement | null>(null);
  const [pinned, setPinned] = useState(false);

  const measure = useCallback(() => {
    const pane = paneRef.current;
    const inner = pane?.firstElementChild as HTMLElement | null;
    if (!enabled || !pane || !inner) {
      setPinned(false);
      return;
    }

    // Are the panes actually side by side RIGHT NOW? CSS owns that answer — the
    // >=980px query, `.aas-grid--stacked` (chat open) and the `.aas-passcol:empty`
    // gate all feed it — so read the COMPUTED direction rather than re-deriving it
    // from props. Stacked panes have no beside-pane to hold in view, and a passage
    // activity keeps the question stacked over the answer, so neither may pin.
    const parent = pane.parentElement;
    if (!parent || window.getComputedStyle(parent).flexDirection !== "row") {
      setPinned(false);
      return;
    }

    // The scrollport a sticky block resolves against: the nearest ancestor that
    // actually scrolls. On every learner surface that is the OUTER pane, not
    // `.aas-layout` — see "WHERE THE SCROLLPORT ACTUALLY IS" above. Reading the
    // computed overflow (rather than naming a class) is what makes this correct on
    // all seven of them without enumerating any.
    //
    // If NOTHING in the chain scrolls, do not pin: with no scrollport there is no
    // scroll for the pin to survive, and `clientHeight` would be meaningless.
    let portHeight: number | null = null;
    let node: HTMLElement | null = parent;
    while (node && node !== document.body) {
      const overflowY = window.getComputedStyle(node).overflowY;
      if (overflowY === "auto" || overflowY === "scroll") {
        portHeight = node.clientHeight || null;
        break;
      }
      node = node.parentElement;
    }
    if (portHeight == null) {
      setPinned(false);
      return;
    }

    setPinned(
      decidePaneFitMode({
        innerHeight: inner.offsetHeight,
        portHeight,
        paneHeight: pane.offsetHeight,
      }) === "pin"
    );
  }, [enabled]);

  // measure() forces layout (getComputedStyle + offsetHeight), and its triggers
  // are frame-rate events: dragging a window edge, or dragging the OpenEnded
  // textarea's `resize: vertical` handle, both fire continuously. Coalesce to one
  // measurement per frame — and keep the state write OUT of the ResizeObserver
  // callback, which is what avoids "ResizeObserver loop completed with undelivered
  // notifications" when an observed subtree reacts to its own measurement.
  const frameRef = useRef<number | null>(null);
  const scheduleMeasure = useCallback(() => {
    if (frameRef.current != null) return;
    frameRef.current = window.requestAnimationFrame(() => {
      frameRef.current = null;
      measure();
    });
  }, [measure]);

  useEffect(() => {
    measure();
    const pane = paneRef.current;
    if (!enabled || !pane) return;

    window.addEventListener("resize", scheduleMeasure);

    // Observe the pane (sized by its taller sibling) AND the inner block, because
    // content height changes with no window resize at all: an image finishing
    // load, or a correct-answer marker appearing once the learner submits.
    // Guarded because ResizeObserver does not exist in jsdom — the resize
    // listener above must still be wired when it is missing.
    let observer: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(scheduleMeasure);
      observer.observe(pane);
      const inner = pane.firstElementChild;
      if (inner) observer.observe(inner);
    }

    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener("resize", scheduleMeasure);
      // Drop a queued frame so it cannot setState after unmount.
      if (frameRef.current != null) {
        window.cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };
  }, [enabled, measure, scheduleMeasure]);

  return [paneRef, pinned] as const;
};
